import { CheckCircleRounded, CloseRounded, RadioButtonUncheckedRounded, WarningRounded } from "@mui/icons-material";
import { MenuItem, Checkbox, Select, Typography, FormControl, Chip, Button, IconButton } from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getChipWidth } from "../../helpers";
import classNames from "classnames";

interface SelectDropdownProps {
  options: string[];
  selectedOptions: string[];
  onSelect: (selected: string[]) => void;
  disabledOptions: string[];
  searchEnabled?: boolean;
  placeholder?: string;
  includeSector?: boolean;
  limit?: number;
  makeOptionAvailable: (option: string) => void;
  resetSelectedOptions: (option: boolean) => void;
  dropdownRef?: React.RefObject<HTMLDivElement>;
}
const ChipList = ({ selectedArr, includeSector }: { selectedArr: string[]; includeSector: boolean }) => {
  const containerWidth = 300;

  const { visibleChips, additionalChipsCount } = useMemo(() => {
    let accumulatedWidth = 0;
    let visibleChipsCount = 0;

    for (let i = 0; i < selectedArr.length; i++) {
      const chipWidth = getChipWidth(selectedArr[i]);
      accumulatedWidth += chipWidth;

      if (accumulatedWidth > containerWidth) break;

      visibleChipsCount++;
    }

    return {
      visibleChips: selectedArr.slice(0, visibleChipsCount),
      additionalChipsCount: selectedArr.length - visibleChipsCount,
    };
  }, [selectedArr]);

  return (
    <div className="flex flex-wrap max-h-[40px] overflow-hidden">
      {visibleChips.map(option => (
        <Chip key={option} label={option} sx={{ padding: 0 }} className={`!m-1 ${includeSector ? "!bg-success-accent !text-success-dark" : "!bg-error-accent !text-error-dark"} `} />
      ))}

      {additionalChipsCount > 0 && (
        <Chip
          key="more-chips"
          label={`+${additionalChipsCount}`}
          className={`!m-1 ${includeSector ? "!bg-success-accent !text-success-dark" : "!bg-error-accent !text-error-dark"} `}
        />
      )}
    </div>
  );
};

const SelectDropdown = ({
  options,
  selectedOptions,
  onSelect,
  disabledOptions,
  placeholder = "Select an option",
  includeSector = false,
  limit = 0,
  makeOptionAvailable,
  resetSelectedOptions,
  dropdownRef,
}: SelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLimitExceedMessage, setShowLimitExceedMessage] = useState(false);
  const isDarkMode = useSelector((state: { theme }) => state.theme.darkMode);

  useEffect(() => {
    if (showLimitExceedMessage) {
      setShowLimitExceedMessage(false);
    }
  }, [selectedOptions, isOpen]);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => {
      const isASelected = selectedOptions.includes(a);
      const isBSelected = selectedOptions.includes(b);
      const isADisabled = disabledOptions.includes(a);
      const isBDisabled = disabledOptions.includes(b);

      // selected options appear first
      if (isASelected && !isBSelected) return -1;
      if (!isASelected && isBSelected) return 1;

      // disabled options appear second
      if (isADisabled && !isBDisabled) return -1;
      if (!isADisabled && isBDisabled) return 1;

      return 0;
    });
  }, [options, selectedOptions, disabledOptions]);

  const handleChange = (event) => {
    const { value } = event.target;
    const filteredValue = value.filter(v => !disabledOptions.includes(v) && v !== undefined);
    if (value.length > limit) {
      setShowLimitExceedMessage(true);
      return;
    }
    onSelect(filteredValue);
  };

  return (
    <div className="w-[292px] md:w-[340px] lg:w-[462px] relative">
      <FormControl style={{ width: "100%" }}>
        <Select
          multiple
          displayEmpty
          value={selectedOptions}
          onChange={handleChange}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return (<p className="text-[13px] font-medium tracking-[0.46px] text-mui-black-60 dark:text-mui-white-70">{placeholder}</p>);
            }
            return (
              <ChipList selectedArr={selected} includeSector={includeSector} />
            );
          }}
          className={classNames(
            "!rounded-full max-h-[42px] select-no-outline",
            { "bg-white dark:bg-background/paper-elevation-8": isOpen,
              "bg-tgpt-secondary-accentLight": !isOpen,
              "!border !border-error-states-outlinedBorder": showLimitExceedMessage,
            },
          )}
          inputProps={{
            "aria-label": "Without label",
            "classes": {
              icon: "dark:text-mui-white-56",
            },
          }}
          MenuProps={{
            PaperProps: {
              ref: dropdownRef,
              className: "custom-scrollbar",
              style: {
                backgroundColor: isDarkMode ? "#2E2E2E" : "white",
                maxHeight: "250px",
                overflowY: "auto",
                borderRadius: "8px",
                boxShadow: "none",
                marginTop: "-10px",
              },
            },
            anchorOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
          }}
        >
          <div className="flex items-center justify-between px-4">
            <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">
              {placeholder}
              {" "}
              (
              {selectedOptions.length && selectedOptions.length}
              )
            </Typography>
            <div className="flex">
              {selectedOptions.length > 0 && (
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  className="!text-transform-none !text-primary-blue-main !min-w-8 !px-2"
                  onClick={() => resetSelectedOptions(includeSector)}
                >
                  Clear
                </Button>
              )}
              <IconButton size="small" onClick={() => setIsOpen(false)}>
                <CloseRounded className="text-mui-black-38 dark:text-mui-white-38" />
              </IconButton>
            </div>
          </div>
          {sortedOptions.map((option) => {
            const isDisabled = disabledOptions.includes(option);
            return (
              <MenuItem
                key={option}
                value={option}
                className="!px-2 !py-0"
                disableRipple
                disabled={isDisabled}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                  "&.Mui-disabled": {
                    "pointerEvents": "auto",
                    "cursor": "not-allowed",
                    "opacity": 1,
                    "position": "relative",
                    "& p": {
                      opacity: 0.38,
                    },
                    "&:hover": {
                      ".remove-button, .remove-button p": {
                        opacity: 1,
                        bgcolor: "transparent",
                      },
                    },
                  },
                  "& .remove-button": {
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                  },
                }}

                onClick={(e) => {
                  if (isDisabled) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                <Checkbox
                  checked={selectedOptions.includes(option)}
                  icon={<RadioButtonUncheckedRounded className="!w-5 !h-5 dark:text-mui-white-56" />}
                  checkedIcon={<CheckCircleRounded className={`!w-5 !h-5 ${includeSector ? "text-text-success-dark" : "text-text-error-dark"}`} />}
                  disabled={isDisabled}
                />
                <div className="flex items-center justify-between w-full">
                  <p
                    className={`text-[13px] ${
                      selectedOptions.includes(option) ? `${includeSector ? "text-text-success-dark" : "text-text-error-dark"}` : "text-black dark:text-white"
                    }`}
                  >
                    {option}
                  </p>
                  <div
                    className="transition-opacity duration-200"
                    onClick={e => e.stopPropagation()}
                  >
                    {isDisabled && (
                      <Button
                        variant="text"
                        size="small"
                        className="!text-transform-none remove-button !min-w-12"
                        onClick={(e) => {
                          e.stopPropagation();
                          makeOptionAvailable(option);
                        }}
                      >
                        <Typography variant="caption/regular-400" className="!text-mui-black-60 dark:!text-mui-white-70">{`Remove from ${includeSector ? "exclusion" : "inclusion"}?`}</Typography>
                      </Button>
                    )}
                  </div>
                </div>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {showLimitExceedMessage && (
        <div className="flex ps-2 pt-2 text-error-main gap-x-1">
          <WarningRounded className="!w-4 !h-4" />
          <Typography variant="caption/regular-400" className="flex text-left !text-[11px] !tracking-[0.06]">You can only select up to 3 sectors to exclude. Deselect a sector to add another.</Typography>
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
